import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import NewHomePage from './pages/NewHomePage';
import JobListPage from './pages/JobListPage';
import JobPage from './pages/JobPage';
import ProjectOverviewPage from './pages/ProjectOverviewPage';
import DomainsOverviewPage from './pages/DomainsOverviewPage';
import DomainDetailPage from './pages/DomainDetailPage';
import FamiliesOverviewPage from './pages/FamiliesOverviewPage';
import FamilyDetailPage from './pages/FamilyDetailPage';
import FAQPage from './pages/FAQPage';
import ImpressumPage from './pages/ImpressumPage';
import DatenschutzPage from './pages/DatenschutzPage';
import DatenschutzBewerberPage from './pages/DatenschutzBewerberPage';
import { ApplicationProvider } from './contexts/ApplicationContext';
import { AppInsightsProvider } from './AppInsightsProvider';
import './App.css';
import { getCompanyFromUrl, isCareerPagePath } from './utils/company';

// Dynamic favicon component
function DynamicFavicon() {
  const location = useLocation();

  // Determine current company from path (same logic as Footer)
  const getCompanyFromPath = (): string => getCompanyFromUrl();

  // Get company-specific favicon
  const getFavicon = (company: string): string => {
    switch (company) {
      case 'talentschmiede':
        return '/talentschmiedefav.png';
      case 'digitaldavid':
        return '/ddfav.png';
      default: // 'dwg'
        return '/logo.png';
    }
  };

  useEffect(() => {
    const company = getCompanyFromPath();
    const faviconUrl = getFavicon(company);

    // Update favicon
    let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/png';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = faviconUrl;
  }, [location.pathname]);

  return null; // This component doesn't render anything
}

function App() {
  return (
    <AppInsightsProvider>
      <Router>
        <ApplicationProvider>
          <PortalContent />
        </ApplicationProvider>
      </Router>
    </AppInsightsProvider>
  );
}

function PortalContent() {
  const { pathname } = useLocation();
  const careerPage = isCareerPagePath(pathname);
  return (
    <div className="App">
      <DynamicFavicon />
      {!careerPage && <Header />}
      <main className="main-content">
        <Routes>
          {/* Render homepage directly at / (no redirect) */}
          <Route path="/" element={<JobListPage />} />
          <Route path="/ueber-uns" element={<NewHomePage />} />

          {/* Default routes (for backwards compatibility) */}
          <Route path="/jobs" element={<JobListPage />} />
          <Route path="/job/*" element={<JobPage />} />
          <Route path="/project/:slug" element={<ProjectOverviewPage />} />
          <Route path="/domain/:domain" element={<DomainDetailPage />} />
          <Route path="/domains" element={<DomainsOverviewPage />} />
          <Route path="/families" element={<FamiliesOverviewPage />} />
          <Route path="/family/:family" element={<FamilyDetailPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="/datenschutz-bewerber" element={<DatenschutzBewerberPage />} />

          {/* Company-specific routes (no guard) */}
          <Route path="/:company" element={<JobListPage />} />
          <Route path="/:company/ueber-uns" element={<NewHomePage />} />
          <Route path="/:company/jobs" element={<JobListPage />} />
          <Route path="/:company/job/*" element={<JobPage />} />
          <Route path="/:company/project/:slug" element={<ProjectOverviewPage />} />
          <Route path="/:company/domain/:domain" element={<DomainDetailPage />} />
          <Route path="/:company/domains" element={<DomainsOverviewPage />} />
          <Route path="/:company/families" element={<FamiliesOverviewPage />} />
          <Route path="/:company/family/:family" element={<FamilyDetailPage />} />
          <Route path="/:company/faq" element={<FAQPage />} />
          <Route path="/:company/impressum" element={<ImpressumPage />} />
          <Route path="/:company/datenschutz" element={<DatenschutzPage />} />
          <Route path="/:company/datenschutz-bewerber" element={<DatenschutzBewerberPage />} />
        </Routes>
      </main>
      {!careerPage && <Footer />}
    </div>
  );
}

export default App;
